import React, { useMemo } from 'react'
import { cn } from '../UI/cn'
import {
  getHomepageStats,
  type HomepageStats,
} from '../../services/recommendations_api'
import { formatCurrency } from '../../utils/formatting'
import { getChartColor } from '../../utils/chartColors'
import { ProgressChart } from '../NewCompanyPage/Sidebar/shared/ProgressChart'
import { useQuery } from '@tanstack/react-query'

const CARD_STYLES = 'bg-surface-alt rounded-lg border border-line p-4'

interface PipelineSnapshotCardProps {
  userId?: number
  className?: string
}

/**
 * Standalone pipeline snapshot card showing open deals, pipeline value,
 * lifetime wins, and a stage breakdown chart.
 */
export const PipelineSnapshotCard: React.FC<PipelineSnapshotCardProps> = ({
  userId,
  className,
}) => {
  const { data: pipelineStats, isLoading } = useQuery<HomepageStats | null>({
    queryKey: ['homepage-stats', userId ?? 'self'],
    queryFn: () => getHomepageStats(userId),
  })

  const pipelineChartItems = useMemo(() => {
    if (!pipelineStats) return []
    const stagesWithDeals = pipelineStats.deals_by_stage.filter(
      (s) => s.count > 0
    )
    const totalDeals = stagesWithDeals.reduce((sum, s) => sum + s.count, 0)
    return stagesWithDeals.map((stage, index) => ({
      name: `${stage.stage_name} · ${formatCurrency(stage.total_value)} · ${stage.count} deal${stage.count !== 1 ? 's' : ''}`,
      value: stage.count,
      color: getChartColor(index),
      percentage:
        totalDeals > 0 ? Math.round((stage.count / totalDeals) * 100) : 0,
    }))
  }, [pipelineStats])

  if (isLoading) {
    return (
      <div className={cn(CARD_STYLES, 'animate-pulse', className)}>
        <div className="h-4 w-32 bg-surface-disabled rounded mb-3" />
        <div className="grid gap-3 grid-cols-3 mb-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-surface-disabled rounded-lg" />
          ))}
        </div>
        <div className="h-24 bg-surface-disabled rounded" />
      </div>
    )
  }

  if (!pipelineStats) return null

  return (
    <div className={cn(CARD_STYLES, className)}>
      <h3 className="text-sm font-medium text-fg-tertiary mb-3">
        Pipeline Snapshot
      </h3>
      <div className="space-y-3">
        <div className="grid gap-3 grid-cols-3">
          <div className="rounded-lg border border-line bg-surface px-3 py-2">
            <p className="text-xs text-fg-secondary uppercase tracking-wide">
              Open Deals
            </p>
            <p className="text-2xl font-semibold text-fg">
              {pipelineStats.open_deals_count}
            </p>
          </div>
          <div className="rounded-lg border border-line bg-surface px-3 py-2">
            <p className="text-xs text-fg-secondary uppercase tracking-wide">
              Pipeline Value (1yr)
            </p>
            <p className="text-2xl font-semibold text-fg">
              {formatCurrency(pipelineStats.pipeline_total_value)}
            </p>
          </div>
          <div className="rounded-lg border border-line bg-surface px-3 py-2">
            <p className="text-xs text-fg-secondary uppercase tracking-wide">
              Lifetime Wins
            </p>
            <p className="text-lg font-semibold text-fg">
              {pipelineStats.won_deals_count} won ·{' '}
              {pipelineStats.lost_deals_count} lost
            </p>
          </div>
        </div>
        <div className="bg-surface rounded-lg border border-line p-3">
          {pipelineChartItems.length > 0 ? (
            <ProgressChart
              label="Pipeline by stage"
              items={pipelineChartItems}
              tooltipValueFormatter={(item) =>
                `${item.value} deal${item.value !== 1 ? 's' : ''}`
              }
              legend={{
                columns: 2,
                showPercentages: true,
              }}
            />
          ) : (
            <p className="text-sm text-fg-secondary text-center">
              No active deals in your pipeline right now.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
